/**
 * [toast-lift] — a page-level bottom band the bottom toast stacks lift
 * above (BSP's palette, Sep 2026: a sticky footer holding the page's main
 * action sat under every bottom-right toast). Any element carrying the
 * attribute counts while it TOUCHES the viewport's bottom edge (a sticky
 * or fixed band; a band left mid-page by a short page does not): its
 * live height — a ResizeObserver per band, re-measured on scroll and on
 * resize — is published as `--genus-lift-bottom` on <body>, which the
 * bottom stacks add to their edge offset (scss/_notify.scss). No position
 * change, no page code: mark the band.
 *
 *   <footer class="app-foot" toast-lift>…</footer>
 *
 * Full reference: docs/libs/genus.md "Notifications" → "Behavior details".
 */

let bands = new Set();
let ro = null;
let frame = 0;

function measure() {
	frame = 0;
	const vh = window.innerHeight;
	let lift = 0;
	for (const el of bands) {
		if (!el.isConnected) { bands.delete(el); if (ro) ro.unobserve(el); continue; }
		if (!el.getClientRects().length) continue;
		const r = el.getBoundingClientRect();
		// touches the bottom edge: the band is what the bottom stacks would cover
		if (r.bottom >= vh - 2 && r.top < vh) lift = Math.max(lift, Math.ceil(vh - r.top));
	}
	const value = `${lift}px`;
	if (document.body && document.body.style.getPropertyValue('--genus-lift-bottom') !== value) {
		document.body.style.setProperty('--genus-lift-bottom', value);
	}
}

const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };

function watch(el) {
	if (bands.has(el)) return;
	bands.add(el);
	if (ro) ro.observe(el);
	schedule();
}

function scan(node) {
	if (!(node instanceof Element)) return;
	if (node.hasAttribute('toast-lift')) watch(node);
	node.querySelectorAll('[toast-lift]').forEach(watch);
}

let started = false;
export function initToastLift() {
	if (started || typeof document === 'undefined') return;
	started = true;
	if (typeof ResizeObserver === 'function') ro = new ResizeObserver(schedule);
	const all = () => scan(document.body);
	if (document.body) all();
	else document.addEventListener('DOMContentLoaded', all, { once: true });
	if (typeof MutationObserver === 'function') {
		new MutationObserver((records) => {
			let removed = false;
			for (const r of records) {
				if (r.type === 'attributes') {
					if (r.target.hasAttribute('toast-lift')) watch(r.target);
					else if (bands.delete(r.target)) { if (ro) ro.unobserve(r.target); removed = true; }
					continue;
				}
				r.addedNodes.forEach(scan);
				if (r.removedNodes.length) removed = true;
			}
			if (removed) schedule();
		}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['toast-lift'] });
	}
	window.addEventListener('resize', schedule, { passive: true });
	window.addEventListener('scroll', schedule, { passive: true, capture: true });
}
